import React, { useState, useEffect } from 'react';
import { Form, Button, Spinner } from 'react-bootstrap';
import Select from 'react-select';
import { toast } from 'react-toastify';
import { DYNAMIC_TABLE_ATTRIBUTE_LIST_BY_TABLE_ID_GET, PARTICULAR_REQUEST_PARAMETER_LIST_GET } from '../../../../api/ApiConst';
import ApiCaller from '../../../../api/ApiCaller';
import ResponseParametersForm from '../components/ResponseParametersForm';

const InsertAction = ({ 
    formData, 
    setFormData, 
    allTableList, 
    selectedRequest, 
    allPreviousSteps, 
    currentStepId 
}) => {
    const [selectedTableColumns, setSelectedTableColumns] = useState([]);
    const [tableColumnsLoading, setTableColumnsLoading] = useState(false);
    const [allTableColumns, setAllTableColumns] = useState([]);
    const [remainingTableColumns, setRemainingTableColumns] = useState([]);
    const [requestParameters, setRequestParameters] = useState([]);
    const [allRequestParameters, setAllRequestParameters] = useState([]);
    const [requestParamSource, setRequestParamSource] = useState(formData?.requestParamSource || ['custom_request']);
    const [responseParameters, setResponseParameters] = useState(formData?.responseParameters || []);
    const [columnAttributes, setColumnAttributes] = useState({});
    const [listInput, setListInput] = useState(formData?.listInput || false);
    const [listInputAttribute, setListInputAttribute] = useState(formData?.listInputAttribute || '');

    // Helper Functions
    const getParameterSourcePrefix = (parameter) => {
        if (!parameter) return '';
        // Check if the parameter has a source field (for step parameters)
        if (parameter.source) return parameter.source;
        if (parameter.label?.includes('Custom Request')) return 'custom_request';
        const stepMatch = parameter.label?.match(/Step (\d+)/);
        if (stepMatch) return `step_${stepMatch[1]}`;
        return 'custom_request';
    };

    const parseMappingString = (mappingValue) => {
        const mappingParts = mappingValue.split('.');
        return {
            sourcePrefix: mappingParts[0] || '',
            parameterName: mappingParts[1] || '',
            attributeName: mappingParts[2] || ''
        };
    };

    const buildMappingString = (sourcePrefix, parameterName, attributeName = '') => {
        const baseMapping = `${sourcePrefix}.${parameterName}`;
        return attributeName ? `${baseMapping}.${attributeName}` : baseMapping;
    };

    const getParameterAttributes = (parameter) => {
        if (!parameter) return [];
        
        if (parameter.value && typeof parameter.value === 'object' && parameter.value.config.attributes) {
            return parameter.value.config.attributes || [];
        }
        
        const foundParameter = allRequestParameters?.find(param => param?.id === parameter?.value);
        
        if (foundParameter?.type === 'OBJECT') {
            return foundParameter?.config?.attributes || foundParameter?.attributes || [];
        }
        
        return foundParameter?.config?.attributes || [];
    };

    // Effects
    useEffect(() => {
        if (formData?.tableId) {
            fetchTableAttributes(formData.tableId);
        }
    }, [formData?.tableId]);

    // Initialize with existing data when component loads
    useEffect(() => {
        if (formData?.tableId && formData?.mappings && Object.keys(formData.mappings).length > 0) {
            // If we already have tableId and mappings, fetch table attributes to initialize the component
            fetchTableAttributes(formData.tableId);
        }
    }, []); // Run only once when component mounts

    // Update requestParamSource when formData changes (for editing)
    useEffect(() => {
        console.log(`requestParamSource useEffect - formData.requestParamSource:`, formData?.requestParamSource);
        console.log(`requestParamSource useEffect - formData.mappings:`, formData?.mappings);
        
        if (formData?.requestParamSource && formData.requestParamSource.length > 0) {
            console.log(`requestParamSource useEffect - setting from formData:`, formData.requestParamSource);
            setRequestParamSource(formData.requestParamSource);
        } else if ((formData?.requestParamSource === null || formData?.requestParamSource === undefined) && formData?.mappings && Object.keys(formData.mappings).length > 0) {
            // If requestParamSource is null/undefined but we have mappings, try to infer the sources
            const sources = new Set();
            Object.values(formData.mappings).forEach(mappingValue => {
                if (mappingValue && typeof mappingValue === 'string') {
                    const { sourcePrefix } = parseMappingString(mappingValue);
                    console.log(`requestParamSource useEffect - parsing mappingValue: ${mappingValue}, sourcePrefix: ${sourcePrefix}`);
                    if (sourcePrefix === 'custom_request') {
                        sources.add('custom_request');
                    } else if (sourcePrefix.startsWith('step_')) {
                        sources.add(sourcePrefix);
                    }
                }
            });
            if (sources.size > 0) {
                console.log(`requestParamSource useEffect - inferred sources:`, Array.from(sources));
                setRequestParamSource(Array.from(sources));
            } else {
                // Default to custom_request if no sources can be inferred
                console.log(`requestParamSource useEffect - defaulting to custom_request`);
                setRequestParamSource(['custom_request']);
            }
        } else if (!formData?.requestParamSource && !formData?.mappings) {
            // If neither requestParamSource nor mappings exist, default to custom_request
            console.log(`requestParamSource useEffect - no data, defaulting to custom_request`);
            setRequestParamSource(['custom_request']);
        }
    }, [formData?.requestParamSource, formData?.mappings]);

    // Update responseParameters when formData changes (for editing)
    useEffect(() => {
        if (formData?.responseParameters) {
            setResponseParameters(formData.responseParameters);
        }
    }, [formData?.responseParameters]);

    // Update listInput when formData changes (for editing)
    useEffect(() => {
        if (formData?.listInput !== undefined) {
            setListInput(formData.listInput);
        }
    }, [formData?.listInput]);

    // Update listInputAttribute when formData changes (for editing)
    useEffect(() => {
        if (formData?.listInputAttribute !== undefined) {
            setListInputAttribute(formData.listInputAttribute || '');
        }
    }, [formData?.listInputAttribute]);

    // Initialize columnAttributes when formData changes (for editing)
    useEffect(() => {
        if (formData?.mappings && Object.keys(formData.mappings).length > 0) {
            const initialColumnAttributes = {};
            Object.keys(formData.mappings).forEach(key => {
                if (key.includes('.')) {
                    const parts = key.split('.');
                    if (parts.length >= 2) {
                        const columnName = parts[0];
                        const attributeName = parts[1];
                        if (!initialColumnAttributes[columnName]) {
                            initialColumnAttributes[columnName] = attributeName;
                        }
                    }
                }
            });
            setColumnAttributes(initialColumnAttributes);
        }
    }, [formData?.mappings]);

    // Initialize selectedTableColumns when formData changes (for editing)
    useEffect(() => {
        if (formData?.mappings && Object.keys(formData.mappings).length > 0 && allTableColumns.length > 0) {
            const mappingKeys = Object.keys(formData.mappings);
            const columnsWithMappings = new Set();
            
            mappingKeys.forEach(key => {
                const mappingValue = formData.mappings[key];
                
                if (mappingValue && mappingValue.trim()) {
                    let columnName;
                    if (key.includes('.')) {
                        columnName = key.split('.')[0];
                    } else {
                        columnName = key;
                    }
                    
                    columnsWithMappings.add(columnName);
                }
            });
            
            const columnsWithMappingsArray = Array.from(columnsWithMappings);
            const selectedColumnsData = allTableColumns.filter(item => columnsWithMappingsArray.includes(item.name));
            setSelectedTableColumns(selectedColumnsData);
        }
    }, [formData?.mappings, allTableColumns]);

    // Ensure requestParameters are loaded when editing with existing data
    useEffect(() => {
        console.log(`Ensure requestParameters useEffect - formData.requestParamSource:`, formData?.requestParamSource);
        console.log(`Ensure requestParameters useEffect - formData.mappings:`, formData?.mappings);
        
        if (formData?.requestParamSource && formData.requestParamSource.length > 0) {
            console.log(`Ensure requestParameters useEffect - loading from requestParamSource:`, formData.requestParamSource);
            formData.requestParamSource.forEach(source => {
                if (source === 'custom_request') {
                    fetchRequestParameters(selectedRequest);
                } else {
                    loadStepParameters(source);
                }
            });
        } else if (formData?.requestParamSource === null && formData?.mappings && Object.keys(formData.mappings).length > 0) {
            // If requestParamSource is null but we have mappings, try to infer and load parameters
            console.log(`Ensure requestParameters useEffect - inferring sources from mappings`);
            const sources = new Set();
            Object.values(formData.mappings).forEach(mappingValue => {
                if (mappingValue && typeof mappingValue === 'string') {
                    const { sourcePrefix } = parseMappingString(mappingValue);
                    console.log(`Ensure requestParameters useEffect - parsing mappingValue: ${mappingValue}, sourcePrefix: ${sourcePrefix}`);
                    if (sourcePrefix === 'custom_request') {
                        sources.add('custom_request');
                    } else if (sourcePrefix.startsWith('step_')) {
                        sources.add(sourcePrefix);
                    }
                }
            });
            
            console.log(`Ensure requestParameters useEffect - inferred sources:`, Array.from(sources));
            
            if (sources.has('custom_request')) {
                console.log(`Ensure requestParameters useEffect - loading custom_request parameters`);
                fetchRequestParameters(selectedRequest);
            }
            
            // Load step parameters for any step sources
            sources.forEach(source => {
                if (source !== 'custom_request') {
                    console.log(`Ensure requestParameters useEffect - loading step parameters for: ${source}`);
                    loadStepParameters(source);
                }
            });
        }
    }, [formData?.requestParamSource, formData?.mappings, selectedRequest, allPreviousSteps]);

    // Handle requestParamSource changes
    useEffect(() => {
        requestParamSource.forEach(source => {
            if (source === 'custom_request') {
                fetchRequestParameters(selectedRequest);
            } else {
                loadStepParameters(source);
            }
        });
    }, [requestParamSource, selectedRequest, allPreviousSteps]);

    useEffect(() => {
        if (allTableColumns?.length > 0 && selectedTableColumns?.length > 0) {
            setRemainingTableColumns(allTableColumns?.filter(col => !selectedTableColumns?.includes(col)));
        } else {
            setRemainingTableColumns([]);
        }
    }, [allTableColumns, selectedTableColumns]);

    // Event Handlers
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        
        if (name === 'tableId') {
            fetchTableAttributes(value);
        }
    };

    const handleListInputChange = (e) => {
        const checked = e.target.checked;
        setListInput(checked);
        setFormData(prev => ({
            ...prev,
            listInput: checked,
            // Clear listInputAttribute if listInput is false
            ...(checked ? {} : { listInputAttribute: '' })
        }));
        // Clear listInputAttribute state if listInput is false
        if (!checked) {
            setListInputAttribute('');
        }
    };

    const handleListInputAttributeChange = (selected) => {
        if (!selected) {
            setListInputAttribute('');
            setFormData(prev => ({
                ...prev,
                listInputAttribute: ''
            }));
            return;
        }

        // Build mapping like `custom_request.ParamName` or `step_2.ParamName`
        const sourcePrefix = getParameterSourcePrefix(selected);
        const parameterName = (selected.label && selected.label.split(' (')[0]) || selected.name || selected.value;
        const mapping = buildMappingString(sourcePrefix, parameterName);

        setListInputAttribute(mapping);
        setFormData(prev => ({
            ...prev,
            listInputAttribute: mapping
        }));
    };

    const handleRequestParamSourceChange = (selectedOptions) => {
        const values = selectedOptions ? selectedOptions?.map(option => option?.value) : ['custom_request'];
        setRequestParamSource(values);
        setFormData((prev) => ({
            ...prev,
            requestParamSource: values,
            mappings: {},
        }));
    };

    const handleAddTableColumn = () => {
        if (formData?.tableColumns?.length > 0) {
            const newColumn = allTableColumns?.find(col => col?.id === formData?.tableColumns);
            setSelectedTableColumns([...selectedTableColumns, newColumn]);
            setFormData((prev) => ({
                ...prev,
                tableColumns: "",
            }));
        }
    };

    const handleDeleteTableColumn = (col) => {
        setSelectedTableColumns(selectedTableColumns.filter(c => c !== col));
        
        const currentMappings = { ...formData.mappings } || {};
        if (currentMappings[col?.name]) {
            delete currentMappings[col.name];
            setFormData((prev) => ({
                ...prev,
                mappings: currentMappings,
            }));
        }
    };

    const handleParameterMappingChange = (selected, col) => {
        const currentMappings = { ...formData.mappings } || {};
        
        if (currentMappings[col?.name]) {
            delete currentMappings[col.name];
        }
        
        Object.keys(currentMappings).forEach(key => {
            if (key.startsWith(`${col.name}.`)) {
                delete currentMappings[key];
            }
        });
        
        if (!selected) {
            setSelectedTableColumns(prev => prev.filter(c => c.name !== col.name));
            setColumnAttributes(prev => {
                const newAttributes = { ...prev };
                delete newAttributes[col.name];
                return newAttributes;
            });
        } else {
            const sourcePrefix = getParameterSourcePrefix(selected);
            const parameterName = selected.label?.split(' ')[0] || selected.name || selected.value || selected.id;
            const parameterMapping = buildMappingString(sourcePrefix, parameterName);
            
            const selectedColumnAttribute = columnAttributes[col.name];
            
            if (selectedColumnAttribute) {
                const mappingKey = `${col.name}.${selectedColumnAttribute}`;
                currentMappings[mappingKey] = parameterMapping;
            } else {
                currentMappings[col.name] = parameterMapping;
            }
        }
        
        setFormData((prev) => ({
            ...prev,
            mappings: currentMappings,
        }));
    };

    const handleAttributeMappingChange = (selected, col) => {
        const currentMappings = { ...formData.mappings } || {};
        const attributeName = selected?.value || '';
        
        let mappingKey = col.name;
        let mappingValue = currentMappings[col.name] || '';
        
        const columnAttributeMappings = Object.keys(currentMappings).filter(key => key.startsWith(`${col.name}.`));
        if (columnAttributeMappings.length > 0) {
            mappingKey = columnAttributeMappings[0];
            mappingValue = currentMappings[mappingKey] || '';
        }
        
        const { sourcePrefix, parameterName } = parseMappingString(mappingValue);
        currentMappings[mappingKey] = buildMappingString(sourcePrefix, parameterName, attributeName);
        
        setFormData((prev) => ({
            ...prev,
            mappings: currentMappings,
        }));
    };

    const handleColumnAttributeChange = (selected, col) => {
        const currentMappings = { ...formData.mappings } || {};
        const attributeName = selected?.value || '';
        
        let existingParameterMapping = currentMappings[col.name];
        
        Object.keys(currentMappings).forEach(key => {
            if (key.startsWith(`${col.name}.`)) {
                existingParameterMapping = currentMappings[key];
            }
        });
        
        if (currentMappings[col.name]) {
            delete currentMappings[col.name];
        }
        
        Object.keys(currentMappings).forEach(key => {
            if (key.startsWith(`${col.name}.`)) {
                delete currentMappings[key];
            }
        });
        
        if (existingParameterMapping && attributeName) {
            const newKey = `${col.name}.${attributeName}`;
            currentMappings[newKey] = existingParameterMapping;
        } else if (existingParameterMapping) {
            currentMappings[col.name] = existingParameterMapping;
        }
        
        setColumnAttributes(prev => ({
            ...prev,
            [col.name]: attributeName
        }));
        
        setFormData((prev) => ({
            ...prev,
            mappings: currentMappings,
        }));
    };

    // API Functions
    const fetchTableAttributes = (tableId) => {
        setTableColumnsLoading(true);
        const requestBody = {
            page: 0,
            size: 100,
            searchFilters: [],
            sortRules: [],
        };
        const apiUrl = DYNAMIC_TABLE_ATTRIBUTE_LIST_BY_TABLE_ID_GET.replaceAll('{dynamic-table-id}', tableId);

        ApiCaller.callPostApi(
            apiUrl,
            requestBody,
            {},
            async (response) => {
                let formAttributes = response?.data?.filter(item => item.formAttribute === true);
                formAttributes = await fetchReferencedTableAttributes(formAttributes);
                
                const existingMappings = formData?.mappings || null;
                initializeSelectedColumns(formAttributes, existingMappings);
                setAllTableColumns(formAttributes);
                setTableColumnsLoading(false);
            },
            (error) => {
                console.error('Error fetching table attributes:', error);
                setSelectedTableColumns([]);
                setTableColumnsLoading(false);
            }
        );
    };

    const fetchReferencedTableAttributes = async (formAttributes) => {
        if (!formAttributes || formAttributes.length === 0) return formAttributes;

        const enhancedAttributes = [...formAttributes];

        for (let i = 0; i < enhancedAttributes.length; i++) {
            const attribute = enhancedAttributes[i];
            
            if (attribute.referenceTable && attribute.referenceTable !== null) {
                try {
                    const referencedAttributes = await fetchReferencedTableAttributesById(attribute.referenceTable);
                    if (referencedAttributes && referencedAttributes.length > 0) {
                        enhancedAttributes[i] = {
                            ...attribute,
                            attributes: referencedAttributes
                        };
                    }
                } catch (error) {
                    console.error(`Error fetching referenced table attributes for ${attribute.name}:`, error);
                }
            }
        }

        return enhancedAttributes;
    };

    const fetchReferencedTableAttributesById = (referenceTableId) => {
        return new Promise((resolve, reject) => {
            const requestBody = {
                page: 0,
                size: 100,
                searchFilters: [],
                sortRules: [],
            };
            const apiUrl = DYNAMIC_TABLE_ATTRIBUTE_LIST_BY_TABLE_ID_GET.replaceAll('{dynamic-table-id}', referenceTableId);

            ApiCaller.callPostApi(
                apiUrl,
                requestBody,
                {},
                (response) => {
                    const referencedAttributes = response?.data?.filter(item => item.formAttribute === true) || [];
                    resolve(referencedAttributes);
                },
                (error) => {
                    console.error('Error fetching referenced table attributes:', error);
                    reject(error);
                }
            );
        });
    };

    const initializeSelectedColumns = (formAttributes, existingMappings = null) => {
        const mappings = existingMappings || formData?.mappings || {};
        
        if (Object.keys(mappings).length > 0) {
            const mappingKeys = Object.keys(mappings);
            const columnsWithMappings = new Set();
            
            mappingKeys.forEach(key => {
                const mappingValue = mappings[key];
                
                if (mappingValue && mappingValue.trim()) {
                    let columnName;
                    if (key.includes('.')) {
                        columnName = key.split('.')[0];
                    } else {
                        columnName = key;
                    }
                    
                    columnsWithMappings.add(columnName);
                }
            });
            
            const columnsWithMappingsArray = Array.from(columnsWithMappings);
            const selectedColumnsData = formAttributes?.filter(item => columnsWithMappingsArray.includes(item.name));
            setSelectedTableColumns(selectedColumnsData);
            
            setColumnAttributes(prev => {
                const initialColumnAttributes = { ...prev };
                mappingKeys.forEach(key => {
                    if (key.includes('.')) {
                        const parts = key.split('.');
                        if (parts.length >= 2) {
                            const columnName = parts[0];
                            const attributeName = parts[1];
                            if (!initialColumnAttributes[columnName]) {
                                initialColumnAttributes[columnName] = attributeName;
                            }
                        }
                    }
                });
                return initialColumnAttributes;
            });
        } else {
            setSelectedTableColumns(formAttributes?.slice(0, 3) || []);
        }
    };

    const fetchRequestParameters = (requestId) => {
        console.log(`fetchRequestParameters called with requestId: ${requestId}`);
        const apiUrl = PARTICULAR_REQUEST_PARAMETER_LIST_GET.replaceAll('{request-id}', requestId);
        console.log(`fetchRequestParameters - apiUrl: ${apiUrl}`);
        
        ApiCaller.callPostApi(
            apiUrl,
            null,
            {},
            (response) => {
                console.log(`fetchRequestParameters response:`, response);
                if (response?.data?.length > 0) {
                    const customRequestParams = response.data.map((item) => ({
                        value: item.id,
                        label: `${item.name} (Custom Request)`,
                    }));

                    // Merge with any existing step params already loaded in state
                    setRequestParameters(prev => {
                        const existingStepParams = (prev || []).filter(p => p?.source && String(p.source).startsWith('step_'));
                        const merged = [...customRequestParams, ...existingStepParams];
                        console.log(`fetchRequestParameters - customRequestParams:`, customRequestParams);
                        console.log(`fetchRequestParameters - existingStepParams:`, existingStepParams);
                        return merged;
                    });
                    setAllRequestParameters(response.data);
                } else {
                    // No custom params; keep whatever step params we have
                    setRequestParameters(prev => (prev || []).filter(p => p?.source && String(p.source).startsWith('step_')));
                }
            },
            (error) => {
                console.error('Error fetching request parameters:', error);
                // If the API fails, try to load parameters from existing mappings
                if (formData?.mappings && Object.keys(formData.mappings).length > 0) {
                    console.log(`fetchRequestParameters - API failed, trying to load from existing mappings`);
                    const inferredParams = [];
                    Object.values(formData.mappings).forEach(mappingValue => {
                        if (mappingValue && typeof mappingValue === 'string') {
                            const { sourcePrefix, parameterName } = parseMappingString(mappingValue);
                            if (sourcePrefix === 'custom_request' && parameterName) {
                                inferredParams.push({
                                    value: parameterName,
                                    label: `${parameterName} (Custom Request)`,
                                });
                            }
                        }
                    });
                    console.log(`fetchRequestParameters - inferred params:`, inferredParams);
                    setRequestParameters(prev => {
                        const existingStepParams = (prev || []).filter(p => p?.source && String(p.source).startsWith('step_'));
                        return [...inferredParams, ...existingStepParams];
                    });
                    setAllRequestParameters(inferredParams);
                } else {
                    // Keep whatever step params we already have
                    setRequestParameters(prev => (prev || []).filter(p => p?.source && String(p.source).startsWith('step_')));
                }
            }
        );
    };

    const loadStepParameters = (source) => {
        console.log(`loadStepParameters called with source: ${source}`);
        console.log(`loadStepParameters - allPreviousSteps:`, allPreviousSteps);
        
        // Handle step_ prefix - extract the numeric ID
        let stepId = source;
        if (source.startsWith('step_')) {
            stepId = source.replace('step_', '');
        }
        
        // Try to match the step by different possible ID formats
        let step = null;
        
        // First try exact match
        step = allPreviousSteps?.find(step => step.id === stepId);
        if (!step) {
            // Try integer conversion
            step = allPreviousSteps?.find(step => step.id === parseInt(stepId));
        }
        if (!step) {
            // Try string comparison
            step = allPreviousSteps?.find(step => String(step.id) === String(stepId));
        }
        
        console.log(`loadStepParameters - found step:`, step);
        
        // Try different possible locations for response parameters
        let stepAttributes = null;
        
        if (step) {
            console.log(`loadStepParameters - step.actions:`, step.actions);
            console.log(`loadStepParameters - step.config:`, step.config);
            console.log(`loadStepParameters - step.responseParameters:`, step.responseParameters);
            
            // Try different possible structures - handle actions array
            if (step.actions && step.actions.length > 0) {
                const action = step.actions[0]; // Get the first action
                console.log(`loadStepParameters - action:`, action);
                console.log(`loadStepParameters - action.config:`, action.config);
                console.log(`loadStepParameters - action.config.responseParameters:`, action.config?.responseParameters);
                console.log(`loadStepParameters - action.responseParameters:`, action.responseParameters);
                console.log(`loadStepParameters - action.config.resultAs:`, action.config?.resultAs);
                
                // Check for response parameters in the action config
                stepAttributes = action?.config?.responseParameters || 
                               action?.responseParameters;
                
                // If still not found, check for resultAs
                if (!stepAttributes || stepAttributes.length === 0) {
                    console.log(`loadStepParameters - checking for resultAs in action.config`);
                    if (action?.config?.resultAs) {
                        console.log(`loadStepParameters - found resultAs in action.config:`, action.config.resultAs);
                        stepAttributes = [{
                            name: action.config.resultAs,
                            type: 'OBJECT'
                        }];
                    }
                }
            } else {
                // Try the old structure
                stepAttributes = step?.action?.config?.responseParameters || 
                               step?.config?.responseParameters ||
                               step?.responseParameters ||
                               step?.action?.responseParameters;
            }
            
            console.log(`loadStepParameters - stepAttributes:`, stepAttributes);
            
            // If still not found, try to look for resultAs or other response data
            if (!stepAttributes || stepAttributes.length === 0) {
                console.log(`loadStepParameters - no stepAttributes found, checking for resultAs`);
                if (step.actions && step.actions.length > 0) {
                    const action = step.actions[0];
                    if (action?.config?.resultAs) {
                        console.log(`loadStepParameters - found resultAs in action.config:`, action.config.resultAs);
                        stepAttributes = [{
                            name: action.config.resultAs,
                            type: 'OBJECT'
                        }];
                    }
                } else if (step?.action?.config?.resultAs) {
                    console.log(`loadStepParameters - found resultAs in step.action.config:`, step.action.config.resultAs);
                    stepAttributes = [{
                        name: step.action.config.resultAs,
                        type: 'OBJECT'
                    }];
                } else if (step?.config?.resultAs) {
                    console.log(`loadStepParameters - found resultAs in step.config:`, step.config.resultAs);
                    stepAttributes = [{
                        name: step.config.resultAs,
                        type: 'OBJECT'
                    }];
                } else if (step?.resultAs) {
                    console.log(`loadStepParameters - found resultAs in step:`, step.resultAs);
                    stepAttributes = [{
                        name: step.resultAs,
                        type: 'OBJECT'
                    }];
                }
            }
        }
        
        // Fallback: infer step object/attributes from existing mappings
        if ((!stepAttributes || stepAttributes.length === 0) && formData?.mappings) {
            const inferredByParam = new Map(); // paramName -> Set(attributes)
            Object.values(formData.mappings).forEach(mappingValue => {
                if (typeof mappingValue !== 'string') return;
                const { sourcePrefix, parameterName, attributeName } = parseMappingString(mappingValue);
                if (sourcePrefix === `step_${stepId}` && parameterName) {
                    if (!inferredByParam.has(parameterName)) inferredByParam.set(parameterName, new Set());
                    if (attributeName) inferredByParam.get(parameterName).add(attributeName);
                }
            });
            if (inferredByParam.size > 0) {
                stepAttributes = Array.from(inferredByParam.entries()).map(([paramName, attrSet]) => ({
                    name: paramName,
                    type: 'OBJECT',
                    attributes: Array.from(attrSet).map(a => ({ name: a, type: 'STRING' }))
                }));
                console.log(`loadStepParameters - inferred stepAttributes from mappings:`, stepAttributes);
            }
        }
        
        const stepParams = [];
        
        if (stepAttributes) {
            stepAttributes.forEach(param => {
                // Add the main parameter; embed attributes so attribute dropdown can render
                stepParams.push({
                    value: { config: { attributes: param?.config?.attributes || param?.attributes || [] } },
                    label: `${param?.name || param} (Step ${stepId})`,
                    name: param?.name || String(param),
                    type: param?.type || 'STRING',
                    source: `step_${stepId}`
                });
            });
        }
        
        console.log(`loadStepParameters - stepParams:`, stepParams);
        
        // Replace existing params from this step source to avoid duplicates, but keep others
        setRequestParameters(prev => {
            const others = (prev || []).filter(p => !(p?.source === `step_${stepId}`));
            const newParams = [...others, ...stepParams];
            console.log(`loadStepParameters - updated requestParameters:`, newParams);
            return newParams;
        });
    };

    const getStepParameters = () => {
        console.log(`getStepParameters - requestParamSource:`, requestParamSource);
        let stepParams = [];
        
        if (!requestParamSource || requestParamSource.length === 0) {
            console.log(`getStepParameters - no requestParamSource, returning empty array`);
            return stepParams;
        }
        
        requestParamSource.forEach(source => {
            if (source !== 'custom_request') {
                console.log(`getStepParameters - processing source: ${source}`);
                
                // Handle step_ prefix - extract the numeric ID
                let stepId = source;
                if (source.startsWith('step_')) {
                    stepId = source.replace('step_', '');
                }
                
                // Try to match the step by different possible ID formats
                let step = null;
                
                // First try exact match
                step = allPreviousSteps?.find(step => step.id === stepId);
                if (!step) {
                    // Try integer conversion
                    step = allPreviousSteps?.find(step => step.id === parseInt(stepId));
                }
                if (!step) {
                    // Try string comparison
                    step = allPreviousSteps?.find(step => String(step.id) === String(stepId));
                }
                
                // Try different possible locations for response parameters
                let stepAttributes = null;
                
                if (step) {
                    console.log(`getStepParameters - found step:`, step);
                    console.log(`getStepParameters - step.actions:`, step.actions);
                    
                    // Try different possible structures - handle actions array
                    if (step.actions && step.actions.length > 0) {
                        const action = step.actions[0]; // Get the first action
                        console.log(`getStepParameters - action:`, action);
                        console.log(`getStepParameters - action.config:`, action.config);
                        console.log(`getStepParameters - action.config.responseParameters:`, action.config?.responseParameters);
                        console.log(`getStepParameters - action.config.resultAs:`, action.config?.resultAs);
                        
                        stepAttributes = action?.config?.responseParameters || 
                                       action?.responseParameters;
                        
                        // If still not found, check for resultAs
                        if (!stepAttributes || stepAttributes.length === 0) {
                            console.log(`getStepParameters - checking for resultAs in action.config`);
                            if (action?.config?.resultAs) {
                                console.log(`getStepParameters - found resultAs in action.config:`, action.config.resultAs);
                                stepAttributes = [{
                                    name: action.config.resultAs,
                                    type: 'OBJECT'
                                }];
                            }
                        }
                    } else {
                        // Try the old structure
                        stepAttributes = step?.action?.config?.responseParameters || 
                                       step?.config?.responseParameters ||
                                       step?.responseParameters ||
                                       step?.action?.responseParameters;
                    }
                    
                    // If still not found, try to look for resultAs or other response data
                    if (!stepAttributes || stepAttributes.length === 0) {
                        if (step.actions && step.actions.length > 0) {
                            const action = step.actions[0];
                            if (action?.config?.resultAs) {
                                stepAttributes = [{
                                    name: action.config.resultAs,
                                    type: 'OBJECT'
                                }];
                            }
                        } else if (step?.action?.config?.resultAs) {
                            stepAttributes = [{
                                name: step.action.config.resultAs,
                                type: 'OBJECT'
                            }];
                        } else if (step?.config?.resultAs) {
                            stepAttributes = [{
                                name: step.config.resultAs,
                                type: 'OBJECT'
                            }];
                        } else if (step?.resultAs) {
                            stepAttributes = [{
                                name: step.resultAs,
                                type: 'OBJECT'
                            }];
                        }
                    }
                }
                
                console.log(`getStepParameters - stepAttributes for ${source}:`, stepAttributes);
                if (stepAttributes) {
                    stepAttributes.forEach(param => {
                        // Add the main parameter
                        stepParams.push({
                            value: param?.name || param,
                            label: `${param?.name || param} (Step ${stepId})`,
                            type: param?.type || 'STRING',
                            source: `step_${stepId}`
                        });
                        
                        // If it's an object type and has attributes, add them too
                        if (param?.type === 'OBJECT' && param?.attributes && param.attributes.length > 0) {
                            param.attributes.forEach(attr => {
                                stepParams.push({
                                    value: `${param.name}.${attr.name}`,
                                    label: `${param.name}.${attr.name} (Step ${stepId})`,
                                    type: attr.type || 'STRING',
                                    source: `step_${stepId}`
                                });
                            });
                        }
                    });
                }
            }
        });
        console.log(`getStepParameters - returning stepParams:`, stepParams);
        return stepParams;
    };

    // Render Components
    const renderTableSelector = () => (
        <Form.Group>
            <Form.Label>Table</Form.Label>
            <Form.Control 
                as="select" 
                name="tableId" 
                value={formData?.tableId || ''} 
                onChange={handleChange}
            >
                <option value="">Select Table</option>
                {allTableList?.map((table) => (
                    <option key={table?.id} value={table?.id}>
                        {table?.name}
                    </option>
                ))}
            </Form.Control>
        </Form.Group>
    );

    const renderListInputSection = () => (
        <Form.Group className="mt-3">
            <div style={{
                border: '1px solid #ced4da',
                borderRadius: '4px',
                padding: '8px 12px',
                backgroundColor: listInput ? '#e7f3ff' : '#fff',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
            }}>
                <input
                    type="checkbox"
                    id="listInput"
                    checked={listInput}
                    onChange={handleListInputChange}
                    style={{
                        width: '16px',
                        height: '16px',
                        border: '1px solid #ced4da',
                        borderRadius: '2px',
                        cursor: 'pointer'
                    }}
                />
                <label htmlFor="listInput" style={{ margin: 0, cursor: 'pointer' }}>
                    List Input
                </label>
            </div>
            {listInput && (
                <div className="mt-2">
                    <Form.Label>Select Attribute Variable</Form.Label>
                    <Select
                        options={requestParameters.map(param => ({
                            value: param.value || param.id,
                            label: param.label || param.name
                        }))}
                        value={(() => {
                            if (!listInputAttribute) return null;
                            // listInputAttribute is mapping like "custom_request.Param" or "step_2.Param"
                            const parts = String(listInputAttribute).split('.');
                            const paramName = parts[1] || listInputAttribute; // fallback
                            const matched = requestParameters.find(p => {
                                const name = (p.label && p.label.split(' (')[0]) || p.name || p.value || p.id;
                                return name === paramName;
                            });
                            return matched ? { value: matched.value || matched.id, label: matched.label || matched.name } : { value: listInputAttribute, label: listInputAttribute };
                        })()}
                        onChange={handleListInputAttributeChange}
                        placeholder="Select Attribute Variable"
                        isClearable
                        className="react-select-container"
                    />
                </div>
            )}
        </Form.Group>
    );

    const renderRequestParameterSource = () => (
        <Form.Group className="mt-3">
            <Form.Label>Request Parameter Source</Form.Label>
            <Select
                isMulti
                options={[
                    { value: 'custom_request', label: 'Custom Request' },
                    ...(allPreviousSteps || [])?.map((step) => ({ 
                        value: `step_${step.id}`, 
                        label: "Step " + step.id 
                    })),
                ]}
                value={
                    requestParamSource && requestParamSource.length > 0
                        ? requestParamSource?.map(source =>
                            source === 'custom_request'
                                ? { value: 'custom_request', label: 'Custom Request' }
                                : allPreviousSteps
                                    ?.map((step) => ({ value: `step_${step.id}`, label: "Step " + step.id }))
                                    ?.find((opt) => opt.value === source)
                        ).filter(Boolean)
                        : []
                }
                onChange={handleRequestParamSourceChange}
                isClearable={false}
                className="react-select-container"
            />
        </Form.Group>
    );

    const renderTableColumnSelector = () => (
        <div className="d-flex align-items-end mb-4">
            <Form.Group style={{ width: '55%' }}>
                <Form.Label>Table Columns</Form.Label>
                <Form.Control 
                    as="select" 
                    name="tableColumns" 
                    value={formData?.tableColumns || ''} 
                    onChange={handleChange}
                >
                    <option value="">Select Table Column</option>
                    {remainingTableColumns?.map((col) => (
                        <option key={col?.id} value={col?.id}>{col?.name}</option>
                    ))}
                </Form.Control>
            </Form.Group>
            <Form.Group className="mx-4">
                <Button variant="primary" onClick={handleAddTableColumn}>
                    Add
                </Button>
            </Form.Group>
        </div>
    );

    const renderParameterSelect = (col, selectedParameter) => {
        const allOptions = [...requestParameters];
        const parameterAttributes = getParameterAttributes(selectedParameter);
        const hasParameterAttributes = parameterAttributes?.length > 0;
        
        let mappingValue = formData?.mappings?.[col.name] || '';
        let columnAttributeMappings = Object.keys(formData?.mappings || {}).filter(key => key.startsWith(`${col.name}.`));
        
        if (columnAttributeMappings.length > 0) {
            mappingValue = formData?.mappings?.[columnAttributeMappings[0]] || '';
        }
        
        const { attributeName } = parseMappingString(mappingValue);

        return (
            <div className="mb-2">
                <Form.Label required className="fw-bold">Select Parameter</Form.Label>
                <Select
                    options={allOptions}
                    isClearable
                    value={selectedParameter}
                    onChange={(selected) => handleParameterMappingChange(selected, col)}
                    placeholder="Select Parameter"
                    className="react-select-container"
                />
                
                {hasParameterAttributes && (
                    <div className="mt-2">
                        <Form.Label className="small text-muted">Select Parameter Attribute</Form.Label>
                        <Select
                            options={parameterAttributes.map(attr => ({
                                value: attr?.name || attr,
                                label: attr?.name || attr
                            }))}
                            isClearable
                            value={attributeName ? { value: attributeName, label: attributeName } : null}
                            onChange={(selected) => handleAttributeMappingChange(selected, col)}
                            placeholder="Select Parameter Attribute"
                            className="react-select-container"
                        />
                    </div>
                )}
            </div>
        );
    };

    const renderMappingRow = (col, idx) => {
        let mappingValue = formData?.mappings?.[col.name] || '';
        let columnAttributeMappings = Object.keys(formData?.mappings || {}).filter(key => key.startsWith(`${col.name}.`));
        
        if (columnAttributeMappings.length > 0) {
            mappingValue = formData?.mappings?.[columnAttributeMappings[0]] || '';
        }
        
        console.log(`renderMappingRow - col.name: ${col.name}, mappingValue: ${mappingValue}`);
        
        const { sourcePrefix, parameterName: mappedParameterName, attributeName } = parseMappingString(mappingValue);
        
        console.log(`renderMappingRow - parsed: sourcePrefix: ${sourcePrefix}, mappedParameterName: ${mappedParameterName}, attributeName: ${attributeName}`);
        
        let selectedParameter = null;

        if (sourcePrefix !== 'table' && mappedParameterName) {
            console.log(`renderMappingRow - looking for parameter: ${mappedParameterName}`);
            console.log(`renderMappingRow - available parameters:`, requestParameters);
            
            // Find the parameter by matching the parameter name
            selectedParameter = requestParameters.find((parameter) => {
                // Check if the parameter name matches
                const paramName = parameter.label?.split(' ')[0] || parameter.name || parameter.value;
                const matches = paramName === mappedParameterName || parameter.value === mappedParameterName || parameter.id === mappedParameterName;
                console.log(`renderMappingRow - checking parameter: ${paramName} vs ${mappedParameterName}, matches: ${matches}`);
                return matches;
            });
            
            // If not found by name, try to find by the full mapping string
            if (!selectedParameter) {
                console.log(`renderMappingRow - trying full mapping string match: ${mappingValue}`);
                selectedParameter = requestParameters.find((parameter) => {
                    const paramSourcePrefix = getParameterSourcePrefix(parameter);
                    const paramName = parameter.label?.split(' ')[0] || parameter.name || parameter.value;
                    const fullMapping = `${paramSourcePrefix}.${paramName}`;
                    const matches = fullMapping === mappingValue;
                    console.log(`renderMappingRow - checking full mapping: ${fullMapping} vs ${mappingValue}, matches: ${matches}`);
                    return matches;
                });
            }
            
            // If still not found, try to find by step source and parameter name
            if (!selectedParameter && sourcePrefix.startsWith('step_')) {
                console.log(`renderMappingRow - trying step source match for ${sourcePrefix}`);
                selectedParameter = requestParameters.find((parameter) => {
                    // Check if the parameter has the correct source and name
                    if (parameter.source === sourcePrefix) {
                        const paramName = parameter.label?.split(' ')[0] || parameter.name || parameter.value;
                        const matches = paramName === mappedParameterName || parameter.value === mappedParameterName;
                        console.log(`renderMappingRow - checking step parameter: ${paramName} vs ${mappedParameterName}, source: ${parameter.source}, matches: ${matches}`);
                        return matches;
                    }
                    return false;
                });
            }
            
            console.log(`renderMappingRow - selectedParameter:`, selectedParameter);
        }

        const parameterAttributes = getParameterAttributes(selectedParameter);
        const hasParameterAttributes = parameterAttributes?.length > 0;
        const selectedOption = selectedParameter;
        
        return (
            <div className="d-flex align-items-start mb-3 p-3 border rounded" key={idx}>
                <div style={{ width: '30%',paddingRight:"5%" }}>
                    <strong className="text-primary">{col?.name}</strong>
                    {col.attributes && col.attributes.length > 0 && (
                        <div className="mt-2">
                            <Select
                                options={col.attributes.map(attr => ({
                                    value: attr?.name || attr,
                                    label: attr?.name || attr
                                }))}
                                isClearable
                                value={columnAttributes[col.name] ? { value: columnAttributes[col.name], label: columnAttributes[col.name] } : null}
                                onChange={(selected) => handleColumnAttributeChange(selected, col)}
                                placeholder="Select Column Attribute"
                                className="react-select-container"
                            />
                        </div>
                    )}
                </div>
                <div style={{ width: '60%' }}>
                    {renderParameterSelect(col, selectedOption)}
                </div>
                <div style={{ width: '10%', display: 'flex', justifyContent: 'flex-end', alignItems: 'flex-start', paddingTop: '8px' }}>
                    <Button 
                        variant="outline-danger" 
                        size="sm" 
                        onClick={() => handleDeleteTableColumn(col)}
                    >
                        <i className="fa fa-trash"></i>
                    </Button>
                </div>
            </div>
        );
    };

    const renderMappings = () => (
        <>
            <Form.Label><b>Map Table Columns to Request parameters</b></Form.Label>
            {renderTableColumnSelector()}

            {tableColumnsLoading ? (
                <div className="d-flex justify-content-center align-items-center" style={{ height: '100px' }}>
                    <Spinner animation="border" variant="primary" />
                </div>
            ) : (
                selectedTableColumns?.map((col, idx) => renderMappingRow(col, idx))
            )}
        </>
    );

    return (
        <>
            {renderTableSelector()}
            {renderRequestParameterSource()}
            {renderListInputSection()}
            <hr style={{ margin: "20px 0 30px" }} />
            <Form.Group className="mt-3">{renderMappings()}</Form.Group>
            <hr style={{ margin: "20px 0 30px" }} />
            <ResponseParametersForm
                responseParameters={responseParameters}
                setResponseParameters={setResponseParameters}
                formData={formData}
                setFormData={setFormData}
            />
        </>
    );
};

export default InsertAction; 